import React, { useEffect, useState } from 'react';
import { Upload, FileText, PenLine, ChevronRight, CheckCircle2, Loader2, Circle } from 'lucide-react';

const STEPS = ['Läser ritningar', 'Tolkar konstruktion', 'Beräknar materialåtgång', 'Jämför priser', 'Skapar sammanställning'];

export const AIProgressCard = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = startedAt ? new Date(startedAt).getTime() : Date.now();
    const id = setInterval(() => setElapsed((Date.now() - t0) / 1000), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const active = Math.min(STEPS.length - 1, Math.floor(elapsed / 35));
  return (
    <div className="op-card-dark relative overflow-hidden p-6 sm:p-8" data-testid="material-ai-progress">
      <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-[#F26B1F]/20 blur-3xl op-glow" />
      <div className="relative grid md:grid-cols-2 gap-8 items-center">
        <div>
          <p className="flex items-center gap-2 text-lg font-bold"><img src="/operio-logo-2.png" alt="" className="w-6 h-6" />Operio AI</p>
          <p className="mt-2 text-xl sm:text-2xl font-semibold leading-snug">Analyserar ritningar och beräknar material…</p>
          <ul className="mt-6 space-y-3">
            {STEPS.map((s, i) => (
              <li key={s} className={`flex items-center gap-3 text-sm ${i <= active ? 'text-white' : 'text-white/35'}`} data-testid={`ai-step-${i}`}>
                {i < active ? <CheckCircle2 size={18} className="text-[#F26B1F]" /> : i === active ? <Loader2 size={18} className="text-[#F26B1F] animate-spin" /> : <Circle size={18} />}{s}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-white/45">Stora projekt med flera ritningar kan ta upp till några minuter. Du kan lämna sidan — kalkylen fortsätter.</p>
        </div>
        <div className="hidden md:flex items-center justify-center">
          <div className="relative w-64 h-64" style={{ perspective: 800 }}>
            <div className="absolute inset-0 rounded-3xl border border-[#F26B1F]/40 op-grid-bg" style={{ transform: 'rotateX(58deg) rotateZ(-38deg)', boxShadow: '0 0 80px rgba(242,107,31,.25)' }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="absolute bg-white/80 border border-white" style={{ left: `${10 + (i % 3) * 28}%`, top: `${15 + Math.floor(i / 3) * 40}%`, width: '24%', height: '32%', transform: `translateZ(${18 + (i % 2) * 14}px)`, opacity: i <= active + 1 ? 0.9 : 0.15, transition: 'opacity .6s' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Choice = ({ icon: Icon, title, sub, onClick, primary, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full flex items-center gap-4 rounded-2xl border p-4 text-left transition-[border-color,transform,box-shadow] hover:-translate-y-0.5 ${primary ? 'border-[#F26B1F] bg-[#F26B1F]/[0.04] shadow-[0_14px_30px_-22px_rgba(242,107,31,1)]' : 'border-stone-200 bg-white hover:border-stone-300'}`}>
    <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${primary ? 'bg-[#F26B1F] text-white' : 'bg-stone-100 text-stone-600'}`}><Icon size={19} /></span>
    <span className="flex-1"><span className="block text-sm font-semibold">{title}</span><span className="block text-xs text-stone-500 mt-0.5">{sub}</span></span>
    <ChevronRight size={17} className="text-stone-400" />
  </button>
);

export const MaterialEmptyState = ({ onStart }) => (
  <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6" data-testid="material-empty-state">
    <div className="op-card-dark relative overflow-hidden p-7 sm:p-9 min-h-[320px] flex flex-col justify-end">
      <div className="absolute inset-0 op-grid-bg" />
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F26B1F]/25 blur-3xl" />
      <div className="relative">
        <p className="op-label !text-white/50">Steg 1 av 4 · Material</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold leading-tight">Från ritning<br />till <span className="text-[#F26B1F]">färdig kalkyl.</span></h2>
        <p className="mt-3 text-sm text-white/65 max-w-md">AI läser ritningarna, bygger en 3D-modell, räknar mängder per byggdel och hämtar aktuella priser från svenska leverantörer.</p>
        <button onClick={() => onStart('files')} className="op-btn-primary mt-6" data-testid="generate-material-btn">Starta ny beräkning <ChevronRight size={16} /></button>
      </div>
    </div>
    <div className="op-card p-6">
      <h3 className="text-base font-bold">Vad vill du använda som underlag?</h3>
      <p className="text-xs text-stone-500 mt-1">Ladda upp ritningar, beskriv projektet eller fyll i uppgifterna manuellt.</p>
      <div className="mt-5 space-y-3">
        <Choice icon={Upload} title="Ladda upp ritningar" sub="PDF, JPG, PNG — ger exakt 3D-modell och mängder" onClick={() => onStart('files')} primary testid="material-start-files" />
        <Choice icon={FileText} title="Beskriv projektet" sub="T.ex. byggbeskrivning eller mängdförteckning" onClick={() => onStart('describe')} testid="material-start-describe" />
        <Choice icon={PenLine} title="Manuell inmatning" sub="Projekttyp, yta och våningar steg för steg" onClick={() => onStart('manual')} testid="material-start-manual" />
      </div>
    </div>
  </div>
);
